import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Footer } from "@/components/Layout";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex flex-col">
    <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px] fade-up">
        <div className="mb-8"><Logo light={false} /></div>
        <div className="card p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-[#5B665F] mt-2">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

const Field = ({ label, testId, ...props }) => (
  <label className="block mb-4">
    <span className="field-label">{label}</span>
    <input className="field" data-testid={testId} required {...props} />
  </label>
);

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return { busy, error, submit };
}

const ErrorMsg = ({ error }) => error ? <p data-testid="auth-error" className="text-sm text-[#8A4B00] bg-[#FDEBD3] rounded-lg px-3 py-2 mb-4">{error}</p> : null;

export function LoginPage() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, submit } = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    setSession(data);
    nav(data.user.platform_role === "admin" ? "/admin/rappels" : "/");
  });
  return (
    <Shell title="Connexion" subtitle="Retrouvez vos sponsors et vos contreparties.">
      <form onSubmit={submit}>
        <ErrorMsg error={error} />
        <Field label="Email" type="email" testId="login-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Mot de passe" type="password" testId="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="flex justify-end -mt-2 mb-5"><Link to="/mot-de-passe-oublie" className="text-sm link" data-testid="forgot-password-link">Mot de passe oublié ?</Link></div>
        <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Connexion…" : "Se connecter"}</button>
      </form>
      <p className="text-sm text-[#5B665F] mt-6">Pas encore client ? <Link to="/#offre" className="link" data-testid="offer-link">Découvrir l'offre</Link></p>
      <p className="text-xs text-[#5B665F] mt-3 bg-[#F4F5F1] rounded-lg px-3 py-2" data-testid="demo-hint">Démo : demo@partenia.fr / Demo2026!</p>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, submit } = useSubmit(async () => {
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  });
  return (
    <Shell title="Mot de passe oublié" subtitle="Indiquez votre email, nous vous envoyons un lien de réinitialisation.">
      {sent ? (
        <p data-testid="forgot-success" className="text-sm bg-[#DFF3E6] text-[#145A36] rounded-lg px-3 py-3">Si un compte existe pour cet email, un lien valable une heure vient d'être envoyé.</p>
      ) : (
        <form onSubmit={submit}>
          <ErrorMsg error={error} />
          <Field label="Email" type="email" testId="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="forgot-submit-button">{busy ? "Envoi…" : "Envoyer le lien"}</button>
        </form>
      )}
      <p className="text-sm mt-6"><Link to="/connexion" className="link" data-testid="back-to-login-link">Retour à la connexion</Link></p>
    </Shell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const { busy, error, submit } = useSubmit(async () => {
    if (f.password !== f.confirm) throw { response: { data: { detail: "Les mots de passe ne correspondent pas" } } };
    await api.post("/auth/reset-password", { token: params.get("token") || "", password: f.password });
    toast.success("Mot de passe modifié. Vous pouvez vous connecter.");
    nav("/connexion");
  });
  return (
    <Shell title="Nouveau mot de passe">
      <form onSubmit={submit}>
        <ErrorMsg error={error} />
        <Field label="Nouveau mot de passe" type="password" minLength={8} testId="reset-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Field label="Confirmer" type="password" minLength={8} testId="reset-confirm-input" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
        <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="reset-submit-button">Enregistrer</button>
      </form>
    </Shell>
  );
}
